/**
 * moduleNavConfig — single source of truth for the 8 authenticated-shell
 * modules. Consumed by:
 *   - ModuleNav.jsx    (persistent horizontal tab bar)
 *   - AppNav.jsx       (hamburger / user-menu Modules section)
 *   - Module pages     (H1 subtitle strap under the page header)
 *
 * When taglines, subtitles, or route mappings need to change, edit here —
 * all three surfaces pick up the new copy on next render.
 *
 * Order below matches the visible left-to-right tab order and the top-to-
 * bottom hamburger order. `key` is used to build stable `data-testid`s.
 *
 * Fields:
 *   - label     — shown as the tab body and hamburger primary line
 *   - tagline   — short hover tooltip (tabs) + subtext (hamburger)
 *   - subtitle  — 1-sentence description rendered directly under each page's
 *                 H1 to orient first-time users
 */
import {
  LayoutDashboard,
  Gauge,
  CreditCard,
  Home,
  Briefcase,
  Compass,
  Handshake,
  FileText,
  FileEdit,
  FolderOpen,
  Target,
  CalendarDays,
} from "lucide-react";

export const MODULES = [
  {
    key: "dashboard",
    label: "Dashboard",
    tagline: "Your financial command center.",
    subtitle:
      "Get a clear view of where you stand, what to do next, and what you can unlock.",
    icon: LayoutDashboard,
    to: "/dashboard",
  },
  {
    key: "readiness",
    label: "Financial Analyst",
    tagline: "Your 0\u2013100 score breakdown.",
    subtitle:
      "Analyze your financial profile and discover your next best opportunities.",
    icon: Gauge,
    to: "/readiness",
  },
  {
    key: "credit",
    label: "Credit Readiness",
    tagline: "Analysis, disputes, tradelines.",
    subtitle:
      "Understand your credit profile, identify improvement opportunities, and prepare for funding.",
    icon: CreditCard,
    to: "/credit-report",
  },
  {
    key: "mortgage",
    label: "Mortgage Readiness",
    tagline: "Your home purchase power and readiness.",
    subtitle:
      "Evaluate your home-buying readiness and estimate your purchase power.",
    icon: Home,
    to: "/mortgage-intake",
  },
  {
    key: "opportunities",
    label: "Funding Opportunities",
    tagline: "What you can qualify for today.",
    subtitle:
      "See business funding, mortgage options, and personal credit products that fit your profile today.",
    icon: Briefcase,
    to: "/opportunities",
  },
  {
    key: "roadmap",
    label: "Financial Blueprint",
    tagline: "Your step-by-step plan.",
    subtitle:
      "Follow your personalized action plan toward stronger financial readiness.",
    icon: Compass,
    to: "/roadmap",
  },
  {
    key: "partners",
    label: "Partners",
    tagline: "Live matches from vetted lenders.",
    subtitle:
      "Match with vetted lenders based on your live financial profile.",
    icon: Handshake,
    to: "/partners",
  },
  {
    key: "grants",
    label: "Grants",
    tagline: "Discover 2026 grants that fit your business.",
    subtitle:
      "Browse verified 2026 grant opportunities, check eligibility, and save the ones worth pursuing.",
    icon: Target,
    to: "/grants",
  },
  {
    key: "documents",
    label: "Documents",
    tagline: "Your Business Document Vault.",
    subtitle:
      "Store the paperwork your business relies on — foundation docs, financials, grant materials, and support files — all in one private place.",
    icon: FolderOpen,
    to: "/documents",
  },
  {
    key: "calendar",
    label: "Calendar",
    tagline: "Grant deadlines, renewals, and document refreshes.",
    subtitle:
      "See every upcoming grant deadline, annual renewal window, and document refresh in one place. Add your own reminders anytime.",
    icon: CalendarDays,
    to: "/calendar",
  },
  {
    key: "proposals",
    label: "Proposals",
    tagline: "Draft grant proposals with a guided 10-step wizard.",
    subtitle:
      "Draft grant proposals with a guided 10-step wizard. Save your work as you go, duplicate a proven proposal for a new grant, and keep every version organised in one place.",
    icon: FileEdit,
    to: "/proposals",
  },
  {
    key: "disputes",
    label: "Dispute Center",
    tagline: "Draft and manage credit disputes.",
    subtitle: "Draft, review, and manage FCRA-based credit dispute letters.",
    icon: FileText,
    to: "/dispute-letters",
  },
];

/**
 * Look up a module's subtitle by its route. Used by page components that
 * render the module's descriptive strap directly beneath their H1.
 */
export function getModuleSubtitle(route) {
  const mod = MODULES.find((m) => m.to === route);
  return mod ? mod.subtitle : null;
}
